/* istanbul ignore file */
import { MSALHolder } from '@/lib/msal';
import {
    ChangeUsersStatusRequest,
    CreateCompanyRequest,
    CreateUserRequest,
    UpdateCompanyAuthSettingsRequest,
    UpdateConsentsRequest,
    UpdateUserRequest,
    UpdateUserRoleRequest,
    UploadCompanyLogoRequest,
    CreateUsersInMultiCompaniesRequest,
    LoginType,
    SharedEmailEmploymentsResponse
} from '@/models/company';
import BaseAPI from './baseServiceV2';
import { AxiosInstance } from 'axios';
import PaymentsAPIServiceV2 from './PaymentsServiceV2';

class UsersAPIServiceV2 extends BaseAPI {
    client!: AxiosInstance;

    static instance: UsersAPIServiceV2;

    private constructor() {
        super();
        (async () => {
            const USER_API_BASE_URL = (
                await MSALHolder.getInstance()
            ).getSpaConfiguration()?.backendUserServiceUrl;
            // deepcode ignore PrototypePollution: Data from the server is trusted
            this.client = this.getClient(USER_API_BASE_URL!);
        })();

        UsersAPIServiceV2.instance = this;
    }

    sendActivationEmail = async (userId: string) => {
        const headers = await this.getAuthHeaders();
        return this.client.post(
            `/api/v2/users/reinvite/${userId}`,
            {},
            { headers }
        );
    };

    getUser = async (userId: string) => {
        const headers = await this.getAuthHeaders();
        return this.client.get(`/api/v2/users/get/${userId}`, { headers });
    };

    getSharedEmailEmployments = async (userId: string) => {
        const headers = await this.getAuthHeaders();
        return this.client.get<SharedEmailEmploymentsResponse>(
            `/api/v2/users/${userId}/shared-email-employments`,
            { headers }
        );
    };

    getCurrentCompany = async () => {
        const headers = await this.getAuthHeaders();
        return this.client.get('/api/v2/companies/current', { headers });
    };

    createUser = async (user: CreateUserRequest) => {
        const headers = await this.getAuthHeaders();
        return this.client.post('/api/v2/users', user, { headers });
    };

    updateUserRole = async (request: UpdateUserRoleRequest) => {
        const headers = await this.getAuthHeaders();
        return this.client.put('/api/v2/user/updateRole', request, { headers });
    };

    getConsents = async () => {
        const headers = await this.getAuthHeaders();
        return this.client.get('/api/v2/consents', { headers });
    };

    postponeConsents = async () => {
        const headers = await this.getAuthHeaders();
        return this.client.put('/api/v2/consents/postpone', null, { headers });
    };

    deleteTermsVersion = async (termsVersionId: string) => {
        const headers = await this.getAuthHeaders();
        return this.client.delete(`/api/termsVersions/${termsVersionId}`, { headers });
    };

    getTermsVersions = async () => {
        const headers = await this.getAuthHeaders();
        return this.client.get('/api/v2/termsVersions/current', { headers });
    };

    getCurrentUser = async () => {
        const headers = await this.getAuthHeaders();
        return this.client.get('/api/v2/users/current', { headers });
    };

    // Company-agnostic: resolves platform-admin status before a company is selected.
    getIdentity = async () => {
        const headers = await this.getAuthHeaders();
        return this.client.get('/api/v2/users/identity', { headers });
    };

    disableUsers = async (
        request: ChangeUsersStatusRequest,
        companyId?: string
    ) => {
        const headers = await this.getAuthHeaders(companyId);
        return this.client.post(`/api/v2/users/disable`, request, { headers });
    };

    updateUsersEnabled = async (
        enabled: boolean,
        request: ChangeUsersStatusRequest,
        companyId?: string
    ) => {
        return enabled
            ? PaymentsAPIServiceV2.getInstance().enableUsers(request, companyId)
            : this.disableUsers(request, companyId);
    };

    updateBackofficeUsersEnabled = async (
        enabled: boolean,
        request: ChangeUsersStatusRequest,
        companyId?: string
    ) => {
        return PaymentsAPIServiceV2.getInstance().updateBackofficeUsersEnabled(
            enabled,
            request,
            companyId
        );
    };

    updateUser = async (user: UpdateUserRequest) => {
        const headers = await this.getAuthHeaders();
        return this.client.post(`/api/v2/users/update/${user.id}`, user, {
            headers
        });
    };

    getSupportedLanguages = async () => {
        const headers = await this.getAuthHeaders();
        return this.client.get('/api/languages', { headers });
    };

    uploadCompanyLogo = async (request: UploadCompanyLogoRequest) => {
        const headers = await this.getAuthHeaders();
        if (headers.headers) {
            // since we are uploading a file, we need to allow the browser to set Content-Type header automatically
            headers.headers['Content-Type'] = undefined;
        }
        return this.client.post(
            '/api/v2/companies/uploadLogo',
            request.formData,
            {
                headers
            }
        );
    };

    uploadLegalDocument = async (formData: FormData) => {
        const headers = await this.getAuthHeaders();
        if (headers.headers) {
            // since we are uploading a file, we need to allow the browser to set Content-Type header automatically
            headers.headers['Content-Type'] = undefined;
        }
        return this.client.post('/api/termsVersion/createWithFile', formData, {
            headers
        });
    };

    updateConsents = async (request: UpdateConsentsRequest) => {
        const headers = await this.getAuthHeaders();
        return this.client.put('/api/v2/consents/update', request, { headers });
    };

    updateCurrentAuthSettings = async (
        request: UpdateCompanyAuthSettingsRequest
    ) => {
        const headers = await this.getAuthHeaders();
        return this.client.put(
            '/api/v2/companies/authSettings/update',
            request,
            {
                headers
            }
        );
    };

    createCompany = async (company: CreateCompanyRequest) => {
        const headers = await this.getAuthHeaders();
        return this.client.post('/api/v2/companies', company, { headers });
    };

    getCompanies = async () => {
        const headers = await this.getAuthHeaders();
        return this.client.get('/api/companies/all', { headers });
    };

    deleteUser = async (userId: string) => {
        const headers = await this.getAuthHeaders();
        return this.client.post(
            `/api/v2/users/delete`,
            {
                userId
            },
            { headers }
        );
    };

    createUserInMultiCompanies = async (
        request: CreateUsersInMultiCompaniesRequest
    ) => {
        const headers = await this.getAuthHeaders();
        return this.client.post('/api/users/companies', request, { headers });
    };

    /**
     * Unauthenticated pre-login lookup.
     * Returns the login type for the given email so the login page can route
     * a cold-start user to the correct MSAL authority (B2C or AD).
     * Returns { loginType: 'AzureB2C' } as the default for unknown emails.
     */
    getLoginType = async (
        email: string
    ): Promise<{ loginType: LoginType; tenantId?: string }> => {
        const response = await this.client.get<{
            loginType: LoginType;
            tenantId?: string;
        }>(`/api/backofficeUser/${encodeURIComponent(email)}`);
        return response.data;
    };

    generateManualOtp = async (userId: string) => {
        const headers = await this.getAuthHeaders();
        return this.client.post<{ email: string; otpCode: string }>(
            `/api/users/otp/manual/${userId}`,
            {},
            { headers }
        );
    };

    static getInstance = () => {
        if (this.instance) {
            return this.instance;
        } else {
            this.instance = new UsersAPIServiceV2();
            return this.instance;
        }
    };
}

export default UsersAPIServiceV2;
